import get from 'lodash/get';
import { Component } from 'react';
import PropTypes from 'prop-types';

import {
  actions as subscribeActions,
  labels as subscribeLabels,
} from '../../constants/analytics/categoryActionLabel/subscribe';

import paymentFlows from '../../constants/payment/paymentFlows';
import vars from '../../vars';

import {
  PAYPAL_PAYMENT_FAILED_MESSAGE,
  PAYPAL_PAYMENT_ACTIVE_IN_ANOTHER_WINDOW,
  PAYPAL_PAYMENT_OPTIONS_INVALID,
} from '../../constants/localizations/subscription';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import PaypalButton from './PaypalButton';

// https://braintree.github.io/braintree-web/3.39.0/paypal_shared_errors.js.html
const paypalPaymentError = {
  PAYPAL_INVALID_PAYMENT_OPTION: PAYPAL_PAYMENT_OPTIONS_INVALID,
  PAYPAL_ACCOUNT_TOKENIZATION_FAILED: PAYPAL_PAYMENT_FAILED_MESSAGE,
  PAYPAL_FLOW_FAILED: PAYPAL_PAYMENT_FAILED_MESSAGE,
  PAYPAL_POPUP_OPEN_FAILED: PAYPAL_PAYMENT_FAILED_MESSAGE,
  'api-error': PAYPAL_PAYMENT_FAILED_MESSAGE,
};

class PaypalPayment extends Component {
  static propTypes = {
    recurly: PropTypes.object.isRequired,
    onSubmitSuccess: PropTypes.func.isRequired,
    onSubmitFailure: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    openFailureDialog: PropTypes.func.isRequired,
    handlePaymentFlowChange: PropTypes.func.isRequired,
    logSubscribeActivity: PropTypes.func.isRequired,
    selectedProduct: PropTypes.object, // not required for PaymentInformation
    currentPaymentFlow: PropTypes.string.isRequired,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);
    this.isPaypalInProgress = this.isPaypalInProgress.bind(this);
    this.onPaypalCancel = this.onPaypalCancel.bind(this);
    this.onPaypalError = this.onPaypalError.bind(this);
    this.onPaypalToken = this.onPaypalToken.bind(this);
    this.startPaypalFlow = this.startPaypalFlow.bind(this);
    this.managePaypalSubmission = this.managePaypalSubmission.bind(this);
  }

  state = {
    // eslint-disable-next-line new-cap
    paypal: this.props.recurly.PayPal({
      braintree: { clientAuthorization: vars.get('BRAINTREE_CLIENT_KEY') },
    }),
  };

  componentDidMount() {
    this.state.paypal.on('cancel', this.onPaypalCancel);
    this.state.paypal.on('error', this.onPaypalError);
    this.state.paypal.on('token', this.onPaypalToken);
  }

  componentWillUnmount() {
    // avoid memory leaks
    this.state.paypal.off('cancel', this.onPaypalCancel);
    this.state.paypal.off('error', this.onPaypalError);
    this.state.paypal.off('token', this.onPaypalToken);
  }

  onPaypalError(error) {
    const { getLocalizedText } = this.context;
    const errorCode = get(error, 'cause.code', '');

    if (paypalPaymentError[errorCode]) {
      this.props.handlePaymentFlowChange(); // pass nothing to default to credit card flow
      this.props.logSubscribeActivity(
        subscribeActions.fail,
        subscribeLabels.paypal,
      );
      this.props.onSubmitFailure(
        error,
        getLocalizedText(paypalPaymentError[errorCode]),
      );
    }
  }

  onPaypalCancel() {
    this.props.handlePaymentFlowChange(); // pass nothing to default to credit card flow
    this.props.logSubscribeActivity(
      subscribeActions.close,
      subscribeLabels.paypal,
    );
  }

  /**
   * The onPaypalToken listener is invoked when the paypal
   * API returns successfully with a recurly token
   *
   * @param token
   * @returns {Promise}
   */
  onPaypalToken(token) {
    this.managePaypalSubmission(token);
  }

  isPaypalInProgress() {
    return this.props.currentPaymentFlow === paymentFlows.paypal;
  }

  /**
   * The manageSubmission calls the onSubmit method
   * with the selectedProduct and recurly token.
   *
   * @param token
   * @returns {Promise}
   */
  async managePaypalSubmission(token) {
    try {
      // The 'await' keyword implicitly wraps result in Promise.resolve
      // if the return value of onSubmit is not a Promise.
      const recurlyPaypalToken = get(token, 'id');
      await this.props.onSubmit(this.props.selectedProduct, recurlyPaypalToken);
      this.props.logSubscribeActivity(
        subscribeActions.success,
        subscribeLabels.paypal,
      );
      this.props.onSubmitSuccess();
    } catch (error) {
      this.props.logSubscribeActivity(
        subscribeActions.fail,
        subscribeLabels.paypal,
      );
      this.props.onSubmitFailure(error);
    }
  }

  /**
   * The startPaypalFlow calls the handlePaymentFlowChange
   * with the paypal flow to set in progress and disable the
   * credit card form.
   * It starts the paypal flow.
   *
   * @param token
   * @returns {Promise}
   */
  startPaypalFlow() {
    if (this.isPaypalInProgress()) {
      this.props.openFailureDialog(
        this.context.getLocalizedText(PAYPAL_PAYMENT_ACTIVE_IN_ANOTHER_WINDOW),
      );
    } else {
      this.state.paypal.start();
      this.props.handlePaymentFlowChange(paymentFlows.paypal);
      this.props.logSubscribeActivity(
        subscribeActions.tap,
        subscribeLabels.paypal,
      );
    }
  }

  render() {
    return (
      <PaypalButton
        handlePaypal={this.startPaypalFlow}
        inProgress={this.isPaypalInProgress()}
      />
    );
  }
}

export default PaypalPayment;
